import { isNumeric } from 'helpers';
import { isNumber } from 'lodash';
import moment, { isMoment } from 'moment';
import 'moment-timezone';
import { timezoneHelpers } from 'providers/TimezoneProvider';

export const momentInstance = moment;

export const msToSeconds = (ms = 0) => {
  if (isNumber(ms)) {
    return ms / 1000;
  }

  return 0;
};

export const setTimeOfDateToDefault = (date, end) => {
  if (moment(date).isValid()) {
    const newDate = date;
    newDate.setHours(end ? 23 : 0);
    newDate.setMinutes(end ? 59 : 0);
    newDate.setSeconds(end ? 59 : 0);
    newDate.setMilliseconds(end ? 59 : 0);

    return newDate;
  }

  return '';
};

export const convertDateByFormat = (date, format = 'YYYY/MM/DD') => {
  if (date) {
    return moment(date).format(format);
  }

  return '';
};

export const convertTime12to24 = (time12h) => {
  const [time, modifier] = time12h.split(' ');

  let [hours, minutes] = time.split(':');

  if (hours === '12') {
    hours = '00';
  }

  if (modifier === 'PM') {
    hours = parseInt(hours, 10) + 12;
  }

  return `${hours}:${minutes}`;
};

export const parseTime24 = (time24h) => {
  const hour = time24h.split(':')[0];
  const minute = time24h.split(':')[1];
  return { hour, minute };
};

export const getTimeStamp = (date) => {
  if (date) {
    return new Date(date).getTime();
  }

  return new Date().getTime();
};

export const getSuggestionSchedule = ({ timeWakeUp = '', timeZone = '' }) => {
  if (!timeWakeUp || !timeZone) {
    return new Date();
  }

  // timeWakeUp is time 12h
  const time24 = convertTime12to24(timeWakeUp);
  const { hour, minute } = parseTime24(time24);

  const currentTimeByTz = moment().tz(timeZone);
  const wakeUpTimeByTz = moment().tz(timeZone).set({ hour, minute, second: 0 });

  // if current time is before wakeUpTime
  if (currentTimeByTz.isBefore(wakeUpTimeByTz)) {
    return new Date(wakeUpTimeByTz.format('MMMM DD YYYY, h:mm:ss A'));
  }

  // else add 1 more day
  return new Date(
    currentTimeByTz
      .set({ hour, minute, second: 0 })
      .add(1, 'days')
      .format('MMMM DD YYYY, h:mm:ss A')
  );
};

export const momentToDateObject = (momentObj) => {
  if (momentObj) {
    const obj = momentObj.toObject();
    const { years, months, date, hours, minutes, seconds, milliseconds } = obj;
    return new Date(years, months, date, hours, minutes, seconds, milliseconds);
  }

  return null;
};

export const getMinDate = (timeZone) => {
  return timeZone
    ? momentToDateObject(moment().tz(timeZone))
    : new Date(moment().format('MMMM DD YYYY, h:mm:ss A'));
};

export const convertDateTime = (time = '') => {
  return moment(time).format('lll');
};

export const parseDateToRequest = (date) => {
  if (date) {
    return convertDateByFormat(date, 'YYYY-MM-DD');
  }

  return null;
};

export const getToday = (format = '') => {
  if (format) {
    return moment().tz(timezoneHelpers.loadTimezoneFromStorage()).format(format);
  }
  return moment().tz(timezoneHelpers.loadTimezoneFromStorage());
};

export const getDaySubtractFromCurrent = (numberDay = 0, format = '') => {
  if (format) {
    if (format === 'dateObj') {
      return moment().subtract(numberDay, 'd').toDate();
    }

    return moment().subtract(numberDay, 'd').format(format);
  }

  return moment().subtract(numberDay, 'd');
};

export const calculateTimeLeft = (
  timeLeft,
  { isMinimize, hideSeconds, hideMinutes } = {
    isMinimize: false,
    hideMinutes: false,
    hideSeconds: false,
  }
) => {
  if (moment(timeLeft).isValid()) {
    const duration = moment.duration(timeLeft);
    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    const daysStr = isMinimize ? 'd' : ' days';
    const hoursStr = isMinimize ? 'h' : ' hours';
    const minutesStr = isMinimize ? 'min' : ' minutes';
    const secondsStr = isMinimize ? 's' : ' seconds';

    if (days) {
      return `${days}${daysStr} ${hours}${hoursStr} ${
        hideMinutes ? '' : `${minutes}${minutesStr}`
      } ${hideSeconds ? '' : `${seconds}${secondsStr}`}`;
    }

    if (hours) {
      return `${hours}${hoursStr} ${hideMinutes ? '' : `${minutes}${minutesStr}`} ${
        hideSeconds ? '' : `${seconds}${secondsStr}`
      }`;
    }

    if (minutes) {
      return `${hideMinutes ? '' : `${minutes}${minutesStr}`} ${
        hideSeconds ? '' : `${seconds}${secondsStr}`
      }`;
    }

    if (isNumeric(seconds)) {
      return `${hideSeconds ? '' : `${seconds}${secondsStr}`}`;
    }

    return '';
  }

  return '';
};

export const timeBetween2Dates = (startDate, endDate, options = { isABS: false }) => {
  const { isABS } = options || {};

  //* startDate + endDate must be moment object
  if (!isMoment(startDate) || !isMoment(endDate)) {
    return 'Date is invalid!';
  }

  const date1 = startDate.milliseconds(0);
  const date2 = endDate.milliseconds(0);

  let diffInDays = date2.diff(date1, 'days');
  let diffInHours = date2.diff(date1, 'hours');
  let diffInMinutes = date2.diff(date1, 'minutes') % 60;
  let diffInSeconds = date2.diff(date1, 'seconds') % 60;

  let remainingHours = endDate.diff(startDate.add(diffInDays, 'days'), 'hours');
  let remainingMinutes = endDate.diff(startDate.add(remainingHours, 'hours'), 'minutes');
  let remainingSeconds = endDate.diff(startDate.add(remainingMinutes, 'minutes'), 'seconds');

  if (isABS) {
    diffInDays = Math.abs(diffInDays);
    diffInHours = Math.abs(diffInHours);
    diffInMinutes = Math.abs(diffInMinutes);
    diffInSeconds = Math.abs(diffInSeconds);

    remainingHours = Math.abs(remainingHours);
    remainingMinutes = Math.abs(remainingMinutes);
    remainingSeconds = Math.abs(remainingSeconds);
  }

  return {
    days: diffInDays,
    hours: diffInHours,
    minutes: diffInMinutes,
    seconds: diffInSeconds,
    remaining: `${diffInDays ? `${diffInDays}d` : ''} ${
      remainingHours ? `${remainingHours}h` : ''
    } ${remainingMinutes ? `${remainingMinutes}m` : ''} ${
      (!diffInDays && !remainingHours && !remainingMinutes) || remainingSeconds
        ? `${remainingSeconds}s`
        : ''
    }`.trim(),
  };
};

//* To use to replace new Date() because new Date() will throw error on Safari iOS
export const newDate = (date) => {
  if (date) {
    return moment(date).toDate();
  }

  return moment().toDate();
};

export const getDurationTime = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return '-';
  }

  const start = moment(startTime);
  const end = moment(endTime);

  const duration = moment.duration(end.diff(start));

  return `${duration.days()}d ${duration.hours()}h ${duration.minutes()}m`;
};

export const utcToLocalTime = (time, format) => {
  return momentInstance(time)
    .utc()
    .format(format || 'lll');
};

export const encodeTime = (time) => {
  return moment(time).format('YYYYMMDDHH');
};

export const decodeTime = (time) => {
  return moment(time, 'YYYYMMDDHHmm').toDate();
};

export const convertDurationStats = (minutes) => {
  const second = minutes * 60;

  const h = Math.floor(second / 3600)
      .toString()
      .padStart(2, '0'),
    m = Math.floor((second % 3600) / 60)
      .toString()
      .padStart(2, '0'),
    s = Math.floor(second % 60)
      .toString()
      .padStart(2, '0');

  return h + ':' + m + ':' + s;
  //return `${h}:${m}:${s}`;
};

//* Using for parse date ranges to put into request body
export const parseDateRangesForFilters = ({ from, to, format = '', timeNow = false }) => {
  if (!from && !to) {
    return {};
  }

  let fromResult = null;
  let toResult = null;

  const timeNowFormat = timeNow ? undefined : 'day';

  if (!!from) {
    fromResult = format
      ? momentInstance(from).startOf(timeNowFormat).format(format)
      : momentInstance(from).startOf(timeNowFormat);
    if (!to) {
      toResult = format
        ? momentInstance().endOf(timeNowFormat).format(format)
        : momentInstance().endOf(timeNowFormat);
    }
  }

  if (!!to) {
    toResult = format
      ? momentInstance(to).endOf(timeNowFormat).format(format)
      : momentInstance(to).endOf(timeNowFormat);
    if (!from) {
      fromResult = format
        ? momentInstance(to).startOf(timeNowFormat).format(format)
        : momentInstance(to).startOf(timeNowFormat);
    }
  }

  return {
    dateFrom: fromResult,
    dateTo: toResult,
  };
};

export function convertLocalDateToUTCIgnoringTimezone(date) {
  const timestamp = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  );

  return new Date(timestamp);
}

export function getTimestampUTC({ date, month, year, hours, minutes }) {
  return new Date(Date.UTC(year, month, date, hours, minutes, 0, 0));
}

export function calculateTimeRemainingCampaign(total, processed, speed) {
  //* Speed (unit per second)
  const remaining = total - processed;
  if (speed <= 0 || remaining <= 0) {
    return 0;
  }

  const timeRemainingInSeconds = remaining / speed;

  const hours = Math.floor(timeRemainingInSeconds / 3600);
  const minutes = Math.floor((timeRemainingInSeconds % 3600) / 60);
  const seconds = Math.floor(timeRemainingInSeconds % 60);

  return { hours, minutes, seconds };
}

export const isValidDate = (date) => {
  return moment(date).isValid();
};

/**
 * Format a date to a string
 * @param {Date|string} date - The date to format
 * @param {string} pattern - The pattern to format the date
 * @returns {string} - The formatted date
 */
export function formatDate(date, pattern) {
  if (!date) return '';

  try {
    let momentInstance;

    // Preserve the original timezone/offset and avoid converting to local time
    if (typeof date === 'number') {
      // Epoch milliseconds represent UTC time
      momentInstance = moment.utc(date);
    } else if (date instanceof Date) {
      // Use the UTC timestamp to avoid local conversion
      momentInstance = moment.utc(date.getTime());
    } else {
      // Keep the zone from the input string (e.g., "+0700", "Z", etc.)
      // If the string has no zone, parseZone treats it as-is without local conversion
      momentInstance = moment.parseZone(date);
    }

    if (!momentInstance.isValid()) {
      console.warn('Invalid date provided to formatDate:', date);
      return '';
    }

    // Convert custom pattern to moment format
    const momentPattern = pattern
      .replace(/yyyy/g, 'YYYY')
      .replace(/MM/g, 'MM')
      .replace(/dd/g, 'DD')
      .replace(/HH/g, 'HH')
      .replace(/mm/g, 'mm')
      .replace(/ss/g, 'ss');

    return momentInstance.format(momentPattern);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return '';
  }
}

export const getUTCOffsetString = (date = new Date()) => {
  const offsetMinutes = -date.getTimezoneOffset();

  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0');
  const minutes = String(absMinutes % 60).padStart(2, '0');

  return `${sign}${hours}:${minutes}`;
};
